import type { AxiosResponse } from 'axios';

import type {
  IOneItemInPrepareForRequest,
  ITodayOffersForRequestFullResponse,
} from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface.ts';
import api from '@/utils/axiosInterceptor.ts';
import type { ApiResponse } from '@/utils/deepmerge.ts';
import { handleApiError } from '@/utils/errorHandler';

import type { IItemPurchaseHistory } from './interfaces/ItemPurchaseHistory.interface';

export async function GetTodayOffersForRequestApi() {
  const response: AxiosResponse<ITodayOffersForRequestFullResponse> =
    await api.get(`/purchases-requests/today-offers`);
  return response?.data;
}

export async function PrepareOfferForRequestApi(id: string) {
  const response: AxiosResponse<IOneItemInPrepareForRequest[]> = await api.get(
    `/purchases-requests/prepare-purchase-request/${id}`,
  );
  return response?.data;
}

export async function UpdateOneRequestItem({
  id,
  itemID,
}: {
  id: string;
  itemID: number;
}) {
  const response: AxiosResponse<IOneItemInPrepareForRequest> = await api.patch(
    `/purchases-requests/update-item-request`,
    {
      id,
      itemID,
    },
  );
  return response?.data;
}

export async function AddOneItemToRequest({
  id,
  itemID,
  discount,
}: {
  id: string;
  itemID: number;
  discount: number;
}) {
  const response: AxiosResponse<IOneItemInPrepareForRequest> = await api.patch(
    `/purchases-requests/add-item-request`,
    {
      id,
      itemID,
      discount,
    },
  );
  return response?.data;
}

export async function PostOneRequest({
  clientID,
  offerID,
  notes,
  items,
  clientOID,
}: {
  clientOID: string;
  clientID: number;
  offerID: string;
  notes: string;
  items: IOneItemInPrepareForRequest[];
}) {
  try {
    const response: AxiosResponse<{
      healthyRequestID: string;
      RequestID: string;
    }> = await api.post(`/purchases-requests/new-request`, {
      clientID,
      clientOID,
      offerID: offerID || '',
      items: items.map((i) => {
        return {
          ...i,
          underRequests: i?.underRequests?.map((z) => z?.requestBodyID),
        };
      }),
      notes,
    });
    return response?.data;
  } catch (error) {
    // Handle error with proper message extraction and toast deduplication
    handleApiError(error, {
      showToast: true,
      showDialog: import.meta.env.DEV, // Show dialog only in development
      context: 'PostOneRequest',
    });
    throw error; // Re-throw to allow caller to handle if needed
  }
}

/**
 * Fetches purchase history for a specific item
 * @param itemId - The healthy item ID to fetch purchase history for
 * @returns A promise that resolves to an array of IItemPurchaseHistory objects
 */
export async function getItemPurchaseHistory(
  itemId: number,
): Promise<IItemPurchaseHistory[]> {
  try {
    const response: AxiosResponse<ApiResponse<IItemPurchaseHistory[]>> =
      await api.get(`/purchases-requests/item-purchase-history/${itemId}`);

    return response?.data?.data || [];
  } catch (error) {
    console.error('Error fetching item purchase history:', error);
    return [];
  }
}
